// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-22) — the headset's auto-rotate: a Spin button on
 * the dock that turns the globe by itself, round the ladder off → ½× →
 * 1× → 2× → 4× → off with each press, as the dock's speed button goes
 * round the playback speeds. 1× is the browser's default auto-rotate
 * (30° every 10 s, `mapRenderer.setRotationRate`), so the two spin alike.
 *
 * The spin is about the globe's own axis (`Object3D.rotateY`, the local
 * Y), eastward as the Earth turns, and `vrScene.update` carries it to the
 * other globes. It waits — without forgetting the choice — while the
 * viewer holds the globe with a trigger, while a fly-to or a room's lead
 * drives it, and while the right stick's zoom is on (a magnified place
 * sliding away would be hard to follow).
 */

/** The speeds a press goes round, slowest first; 0 is off. */
export const SPIN_RATES = [0, 0.5, 1, 2, 4] as const

/** 1× in radians per second: 30° every 10 s. */
const RADIANS_PER_SECOND_AT_1X = (Math.PI / 6) / 10

const EPS = 1e-3

/** The rate after one press: the next step up, then off again. */
export function nextSpinRate(current: number): number {
  return SPIN_RATES.find((r) => r > current + EPS) ?? 0
}

/** How far the globe turns in a frame, in radians, at a rate for `dtSeconds`. */
export function spinRadians(rate: number, dtSeconds: number): number {
  if (!(rate > 0) || !(dtSeconds > 0)) return 0
  return rate * RADIANS_PER_SECOND_AT_1X * dtSeconds
}

/** The rate as the dock's button shows it: "½", "1", "2", "4". */
export function spinRateText(rate: number): string {
  if (Math.abs(rate - 0.5) < EPS) return '½'
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}
